import { Controller } from "@hotwired/stimulus";
import bootstrap from "bootstrap/dist/js/bootstrap";
import { scanImpressions } from "../utils/ux_event_tracker";

let placesLoader = null;

// Google's loading=async pattern signals readiness through a global callback
// instead of the script's onload, which fires before the libraries are parsed.
const PLACES_CALLBACK = "ocPlacesReady";

// Lifetime of the "browse the menu first" cookie (name comes from the server,
// which reads it back in needs_delivery_address?).
const SKIP_TTL_SECONDS = 1800;

// Step 2 of the ordering funnel: the delivery address, taken inside the
// order-type modal as a second pane. Lives on the layout root (not the modal) so
// the header pills can open it too.
//
// The checkout page reuses this same controller for its change-address modal,
// where there is no type pane above the address one and a confirmed save has to
// re-render the receipt — see the hasTypePaneTarget guards and reloadOnConfirm.
export default class extends Controller {
  static targets = [
    "modal",
    "header",
    "typePane",
    "addressPane",
    "input",
    "zipcode",
    "city",
    "state",
    "latitude",
    "longitude",
  ];

  static values = {
    startOnAddress: Boolean,
    placesUrl: String,
    skipCookie: String,
    reloadOnConfirm: Boolean,
  };

  connect() {
    if (this.startOnAddressValue) this.showAddressPane();
  }

  disconnect() {
    this.#releaseAutocomplete();
  }

  // Delivery was chosen — from the modal option or a header pill. The server
  // stamps data-oc-needs-address on the link, so this only reads the answer.
  openIfNeeded(event) {
    if (event.currentTarget.dataset.ocNeedsAddress === "true") {
      this.#modal()?.show();
      this.showAddressPane();
    } else {
      this.closeModal();
    }
  }

  showAddressPane() {
    if (!this.hasAddressPaneTarget) return;

    this.paneShown = true;
    // The checkout modal has no type pane above the address one — by then the
    // order type is settled and the address form is all the modal holds.
    if (this.hasTypePaneTarget) this.typePaneTarget.classList.add("d-none");
    this.addressPaneTarget.classList.remove("d-none");
    // The pane carries its own heading; leaving "Choose order type" above it
    // would describe a step the customer has already finished.
    this.#toggleHeader(false);
    this.#activateAutocomplete();
    if (this.hasInputTarget) this.inputTarget.focus();
  }

  // Also the modal's hidden.bs.modal handler: nothing else rewinds the pane, so
  // skipping the address step and then reopening the modal from a header pill
  // would come back to the address form instead of the order-type list.
  showTypePane() {
    if (!this.hasTypePaneTarget) return;

    this.addressPaneTarget.classList.add("d-none");
    this.typePaneTarget.classList.remove("d-none");
    this.#toggleHeader(true);
  }

  // Swap in the pane the server re-rendered: either the confirmed summary or the
  // form carrying an error. Autocomplete re-binds itself through
  // inputTargetConnected once Stimulus picks up the new markup.
  paneUpdated(event) {
    const [, , xhr] = event.detail;
    this.addressPaneTarget.innerHTML = xhr.response;
    // A new address moves the Uber quote, which moves the delivery fee row, the
    // TOTAL and the amount the PaymentIntent is opened for. Re-rendering the page
    // is cheaper and more honest than re-deriving the receipt here.
    if (this.reloadOnConfirmValue && this.#addressConfirmed()) {
      window.location.reload();
      return;
    }
    // The swapped-in pane can carry impressions (Uber quote error, fee shown)
    // the connect-time scan never saw. No-op when tracking isn't mounted —
    // same pattern as the ordering layout controllers' modal swaps.
    scanImpressions(this.addressPaneTarget);
    this.#syncNeedsAddress();
  }

  editAddress() {
    this.#showForm();
    if (this.hasInputTarget) this.inputTarget.focus();
  }

  // The order type moved off delivery. The address survives the switch, so the
  // pane keeps its confirmed summary — just rewind to the type list for the next
  // time the modal opens, and re-read the stamps from what the pane is showing.
  typeChanged() {
    if (!this.hasAddressPaneTarget) return;

    this.showTypePane();
    this.#syncNeedsAddress();
  }

  skipForNow() {
    document.cookie = `${this.skipCookieValue}=1; path=/; max-age=${SKIP_TTL_SECONDS}; samesite=lax`;
    this.closeModal();
  }

  closeModal() {
    this.#modal()?.hide();
  }

  switchToTakeout() {
    this.modalTarget.querySelector('[data-value="takeout"]')?.click();
  }

  inputTargetConnected(input) {
    this.pendingInput = input;
    this.#activateAutocomplete();
  }

  // Autocomplete is an enhancement, never a gate: when Maps can't load the
  // customer still types an address and the server geocodes it (geo falls back to
  // geocode_customer_address when no coordinates arrive).
  //
  // Deferred until the pane is actually on screen. The pane ships with the page,
  // hidden, so binding as soon as the input connects would pull Google's bundle
  // down on every menu page view — for every visitor to a delivery restaurant,
  // most of whom never open the address step.
  #activateAutocomplete() {
    const input = this.pendingInput;
    if (!this.paneShown || !input) return;

    this.pendingInput = null;
    this.#loadPlaces()
      .then(() => this.#bindAutocomplete(input))
      .catch(() => console.warn("[delivery-address] Places unavailable"));
  }

  #bindAutocomplete(input) {
    // The previous input is already out of the DOM by now (paneUpdated replaced
    // it), so whatever it left behind is orphaned.
    this.#releaseAutocomplete();
    const autocomplete = new google.maps.places.Autocomplete(input);
    this.autocomplete = autocomplete;

    input.addEventListener("keydown", (event) => {
      if (event.key === "Enter") event.preventDefault();
    });

    // Editing after picking a suggestion invalidates what that pick resolved.
    // Drop it so the server geocodes what was actually typed rather than trusting
    // the previous address's coordinates.
    input.addEventListener("input", () => this.#clearResolvedFields());

    autocomplete.addListener("place_changed", () => {
      const place = autocomplete.getPlace();
      if (!place.geometry) return;

      const component = (type, key = "long_name") =>
        place.address_components.find((c) => c.types.includes(type))?.[key];

      this.#setField(this.zipcodeTarget, component("postal_code"));
      this.#setField(this.cityTarget, component("locality"));
      this.#setField(
        this.stateTarget,
        component("administrative_area_level_1", "short_name")
      );
      this.#setField(this.latitudeTarget, place.geometry.location.lat());
      this.#setField(this.longitudeTarget, place.geometry.location.lng());
    });
  }

  // Google attaches each Autocomplete's suggestion list to <body> and never takes
  // it back, so re-binding after a pane swap would pile up one dead .pac-container
  // per swap. This controller owns the only address input on the page, so any
  // container still standing here belongs to the instance being dropped.
  #releaseAutocomplete() {
    if (!this.autocomplete) return;

    google.maps.event.clearInstanceListeners(this.autocomplete);
    this.autocomplete = null;
    document.querySelectorAll(".pac-container").forEach((el) => el.remove());
  }

  #setField(field, value) {
    field.value = value == null ? "" : value;
  }

  #clearResolvedFields() {
    [
      this.zipcodeTarget,
      this.cityTarget,
      this.stateTarget,
      this.latitudeTarget,
      this.longitudeTarget,
    ].forEach((field) => this.#setField(field, ""));
  }

  // Loaded on demand, once per page, so the menu page isn't charged for the
  // Maps bundle unless the address pane is actually used.
  #loadPlaces() {
    if (window.google?.maps?.places) return Promise.resolve();

    if (!placesLoader) {
      placesLoader = new Promise((resolve, reject) => {
        window[PLACES_CALLBACK] = resolve;
        const script = document.createElement("script");
        script.src = `${this.placesUrlValue}&loading=async&callback=${PLACES_CALLBACK}`;
        script.async = true;
        script.onerror = (error) => {
          // Drop the rejected promise so reopening the pane can try again —
          // caching it would make one blocked request poison the whole page.
          placesLoader = null;
          script.remove();
          reject(error);
        };
        document.head.appendChild(script);
      });
    }

    return placesLoader;
  }

  // The delivery option and pills were stamped with data-oc-needs-address when
  // the page rendered; once an address is stored that answer is stale. Read from
  // the swapped-in markup rather than a target, which Stimulus registers async.
  #syncNeedsAddress() {
    const stored = this.#addressConfirmed();

    this.element.querySelectorAll("[data-oc-needs-address]").forEach((el) => {
      el.dataset.ocNeedsAddress = stored ? "false" : "true";
    });
  }

  // The pane is showing a stored, deliverable address rather than the entry form.
  #addressConfirmed() {
    const confirmed = this.#state("confirmed");
    return !!confirmed && !confirmed.classList.contains("d-none");
  }

  #toggleHeader(visible) {
    if (!this.hasHeaderTarget) return;

    this.headerTarget.classList.toggle("d-none", !visible);
  }

  #showForm() {
    this.#state("confirmed")?.classList.add("d-none");
    this.#state("form")?.classList.remove("d-none");
  }

  #state(name) {
    return this.addressPaneTarget.querySelector(`[data-address-state="${name}"]`);
  }

  #modal() {
    if (!this.hasModalTarget) return null;

    return bootstrap.Modal.getOrCreateInstance(this.modalTarget);
  }
}
